import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import type { MealPlanResponse, PriceSource } from '@meal-planner/shared';

/**
 * Public shared view — no auth, no navigation, no user-specific UI.
 * Renders only the snapshot data for the shared resource.
 * Instructions rendered as plain text list — never dangerouslySetInnerHTML.
 */

interface MealPlanSnapshot {
  type: 'meal_plan';
  weekStart: string;
  plan: MealPlanResponse;
}

interface ShoppingListSnapshot {
  type: 'shopping_list';
  list: {
    items: {
      name: string;
      quantity: number;
      unit: string;
      estimatedPrice: number | null;
      priceSource?: PriceSource | null;
    }[];
    estimatedTotal?: number | null;
  };
}

function sharedSourceBadge(source: PriceSource | null | undefined): { label: string; tone: 'live' | 'estimate' } | null {
  if (source === 'kroger') return { label: 'live', tone: 'live' };
  if (source === 'usda' || source === 'bls') return { label: 'estimate', tone: 'estimate' };
  return null;
}

type Snapshot = MealPlanSnapshot | ShoppingListSnapshot;

export function SharedViewPage() {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, isError } = useQuery<Snapshot>({
    queryKey: ['shared', token],
    queryFn: () => api.get(`/api/shared/${token}`),
    enabled: !!token,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <span className="text-lg font-semibold text-emerald-600">Platterplan</span>
        <span className="text-xs text-gray-400 ml-3">Shared view</span>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {isError && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-sm">
              This link has expired or is no longer available.
            </p>
          </div>
        )}

        {data && data.type === 'meal_plan' && (
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">
              Meal Plan — Week of{' '}
              {new Date(data.weekStart).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </h1>
            <div className="space-y-4">
              {data.plan.days.map((day) => (
                <div key={day.date} className="card overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-100 px-5 py-2">
                    <p className="text-sm font-medium text-gray-700">
                      {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {day.meals.map((meal, i) => (
                      <div key={i} className="px-5 py-3">
                        <div className="flex gap-2 items-center mb-1">
                          <span className="text-xs text-emerald-600 font-medium capitalize">{meal.type}</span>
                          <span className="text-sm font-medium text-gray-800">{meal.name}</span>
                        </div>
                        <ul className="flex flex-wrap gap-1 mb-2">
                          {meal.ingredients.map((ing, j) => (
                            <li key={j} className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                              {ing.quantity} {ing.unit} {ing.name}
                            </li>
                          ))}
                        </ul>
                        <ol className="list-decimal list-inside space-y-0.5">
                          {meal.instructions.map((step, j) => (
                            <li key={j} className="text-xs text-gray-600">{step}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data && data.type === 'shopping_list' && (
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">Shopping List</h1>
            {data.list.estimatedTotal != null && (
              <div className="mb-4 text-sm text-gray-600">
                Estimated total:{' '}
                <span className="font-medium text-gray-900">
                  ${data.list.estimatedTotal.toFixed(2)}
                </span>
              </div>
            )}
            <div className="card divide-y divide-gray-100">
              {data.list.items.map((item, i) => {
                const badge = sharedSourceBadge(item.priceSource);
                return (
                  <div key={i} className="flex justify-between items-center px-5 py-3 gap-3">
                    <span className="text-sm text-gray-800">
                      {item.name} — {item.quantity} {item.unit}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      {item.estimatedPrice != null ? (
                        <>
                          <span className="text-sm text-gray-600">${item.estimatedPrice.toFixed(2)}</span>
                          {badge && (
                            <span
                              className={
                                'inline-block px-1.5 py-px rounded-full text-[9px] uppercase tracking-wider font-semibold ' +
                                (badge.tone === 'live'
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-gray-100 text-gray-500')
                              }
                            >
                              {badge.label}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">TBD</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
